import React, { useState, useMemo, useCallback } from 'react'
import { Plus, Search, Edit, Trash2, Fuel, IndianRupee, Calendar, Clock, Truck } from 'lucide-react'
import { useData } from '../context/DataContext'
import FuelForm from '../components/FuelForm'
import Modal from '../components/Modal'
import DeleteConfirmModal from '../components/DeleteConfirmModal'

function FuelManagement() {
  const { state, addFuelEntry, updateFuelEntry, deleteFuelEntry } = useData()
  const { fuelEntries, vehicles, loading, error } = state

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingFuelEntry, setEditingFuelEntry] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterVehicle, setFilterVehicle] = useState('all')
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, fuelEntry: null })

  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  })

  // Memoized filtered fuel entries for better performance
  const filteredFuelEntries = useMemo(() => {
    let filtered = fuelEntries || []
    
    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(entry =>
        entry.bunkName?.toLowerCase().includes(term) ||
        entry.billNumber?.toLowerCase().includes(term) ||
        entry.paymentMethod?.toLowerCase().includes(term) ||
        entry.vehicleNumber?.toLowerCase().includes(term)
      )
    }
    
    // Filter by vehicle
    if (filterVehicle !== 'all') {
      filtered = filtered.filter(entry => 
        entry.fuelItems && entry.fuelItems.some(item => item.vehicleId === filterVehicle)
      )
    }
    
    // Filter by date range
    if (dateRange.startDate) {
      filtered = filtered.filter(entry => 
        new Date(entry.fuelDate) >= new Date(dateRange.startDate)
      )
    }
    
    if (dateRange.endDate) {
      filtered = filtered.filter(entry => 
        new Date(entry.fuelDate) <= new Date(dateRange.endDate)
      )
    }
    
    return filtered
  }, [fuelEntries, searchTerm, filterVehicle, dateRange])

  // Calculate summary statistics
  const totalLiters = filteredFuelEntries.reduce((total, entry) => {
    const fuelItemsTotal = entry.fuelItems ? entry.fuelItems.reduce((sum, item) => sum + parseFloat(item.liters || 0), 0) : 0
    return total + fuelItemsTotal
  }, 0)

  const totalAmount = filteredFuelEntries.reduce((total, entry) => {
    const fuelItemsTotal = entry.fuelItems ? entry.fuelItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) : 0
    return total + fuelItemsTotal
  }, 0)

  const totalGivenToBunk = filteredFuelEntries.reduce((total, entry) => total + parseFloat(entry.amountGivenToBunk || 0), 0)

  // Memoized handlers to prevent unnecessary re-renders
  const handleAddFuelEntry = useCallback(() => {
    setEditingFuelEntry(null)
    setIsModalOpen(true)
  }, [])

  const handleEditFuelEntry = useCallback((fuelEntry) => {
    setEditingFuelEntry(fuelEntry)
    setIsModalOpen(true)
  }, [])

  const handleDeleteFuelEntry = useCallback(async (fuelEntry) => {
    const result = await deleteFuelEntry(fuelEntry.id)
    if (!result.success) {
      alert(`Error deleting fuel entry: ${result.error}`)
    }
  }, [deleteFuelEntry])

  const openDeleteModal = useCallback((fuelEntry) => {
    setDeleteModal({ isOpen: true, fuelEntry })
  }, [])

  const closeDeleteModal = useCallback(() => {
    setDeleteModal({ isOpen: false, fuelEntry: null })
  }, [])

  const confirmDelete = useCallback(() => {
    if (deleteModal.fuelEntry) {
      handleDeleteFuelEntry(deleteModal.fuelEntry)
      closeDeleteModal()
    }
  }, [deleteModal.fuelEntry, handleDeleteFuelEntry, closeDeleteModal])

  const handleSubmit = useCallback(async (fuelData) => {
    try {
      if (editingFuelEntry) {
        const result = await updateFuelEntry(editingFuelEntry.id, fuelData)
        if (result.success) {
          setIsModalOpen(false)
          setEditingFuelEntry(null)
        } else {
          alert(`Error updating fuel entry: ${result.error}`)
        }
      } else {
        const result = await addFuelEntry(fuelData)
        if (result.success) {
          setIsModalOpen(false)
          setEditingFuelEntry(null)
        } else {
          alert(`Error creating fuel entry: ${result.error}`)
        }
      }
    } catch (error) {
      console.error('Error submitting fuel entry:', error)
      alert('An error occurred while saving the fuel entry. Please try again.')
    }
  }, [editingFuelEntry, addFuelEntry, updateFuelEntry])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setEditingFuelEntry(null)
  }, [])

  // Show loading state during operations
  const isUpdating = loading && editingFuelEntry
  const isCreating = loading && !editingFuelEntry && isModalOpen

  // Memoized filter handlers
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value)
  }, [])



  const handleDateRangeChange = useCallback((field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }))
  }, [])

  const clearFilters = useCallback(() => {
    setSearchTerm('')
    setFilterVehicle('all')
    setDateRange({ startDate: '', endDate: '' })
  }, [])

  return (
    <div className="container mx-auto px-4 py-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Fuel Management</h1>
          <p className="text-gray-600">Track fuel consumption and bunk payments</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleAddFuelEntry}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Add Fuel Entry</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Fuel className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Fuel Entries</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">{filteredFuelEntries.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Fuel className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Liters</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">{totalLiters.toFixed(2)} L</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Amount</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">₹{totalAmount.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Given to Bunk</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">₹{totalGivenToBunk.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 my-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search fuel entries by bunk name, bill number, payment method..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="filter-input pl-10 text-sm sm:text-base"
            />
          </div>

          {/* Vehicle Filter */}
          <div className="w-full sm:w-48">
            <select
              value={filterVehicle}
              onChange={(e) => setFilterVehicle(e.target.value)}
              className="filter-input text-gray-900 text-sm sm:text-base"
            >
              <option value="all" className="text-gray-900">All Vehicles</option>
              {vehicles && vehicles
                .sort((a, b) => a.registrationNumber.localeCompare(b.registrationNumber))
                .map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id} className="text-gray-900">
                    {vehicle.registrationNumber} - {vehicle.type}
                  </option>
                ))}
            </select>
          </div>

          {/* Date Range Filters */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="w-full sm:w-40">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                className="filter-input text-sm sm:text-base"
                placeholder="Start Date"
              />
            </div>
            <div className="w-full sm:w-40">
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                className="filter-input text-sm sm:text-base"
                placeholder="End Date"
              />
            </div>
          </div>
        </div>
        
        {/* Clear Filters Button */}
        <div className="flex justify-center sm:justify-end">
          <button
            onClick={clearFilters}
            className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 font-medium"
          >
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8 sm:py-12">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-brand-red mx-auto"></div>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600">Loading fuel entries...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
          <p className="text-sm sm:text-base text-red-800">Error: {error}</p>
        </div>
      )}

      {/* Fuel Entries List */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          {filteredFuelEntries.length > 0 ? (
            filteredFuelEntries.map(fuelEntry => (
              <div key={fuelEntry.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 lg:p-6 hover:shadow-md transition-shadow">
                {/* Fuel Entry Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 truncate">
                      {fuelEntry.bunkName}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 mb-2 truncate">{fuelEntry.paymentMethod}</p>
                    {fuelEntry.fuelItems && fuelEntry.fuelItems.length > 0 && (
                      <div className="space-y-1">
                        {fuelEntry.fuelItems.map((item, index) => (
                          item.vehicleId && item.vehicleNumber && item.vehicleType && (
                            <p key={index} className="text-xs sm:text-sm text-blue-600 font-medium truncate">
                              <Truck className="inline h-3 w-3 mr-1 flex-shrink-0" />
                              <span className="truncate">{item.vehicleNumber} - {item.vehicleType}</span>
                            </p>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-1 sm:space-x-2 flex-shrink-0">
                    <button
                      onClick={() => handleEditFuelEntry(fuelEntry)}
                      className="p-1.5 sm:p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                      title="Edit fuel entry"
                    >
                      <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                    </button>
                    {/* Delete button hidden */}
                    {/* <button
                      onClick={() => openDeleteModal(fuelEntry)}
                      className="p-1.5 sm:p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      title="Delete fuel entry"
                    >
                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                    </button> */}
                  </div>
                </div>



                {/* Fuel Entry Information */}
                <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                  {fuelEntry.fuelDate && (
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                      <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{new Date(fuelEntry.fuelDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {fuelEntry.billNumber && (
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                      <IndianRupee className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0" />
                      <span className="truncate">Bill: {fuelEntry.billNumber}</span>
                    </div>
                  )}
                  {fuelEntry.fuelItems && fuelEntry.fuelItems.length > 0 && (
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                      <IndianRupee className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0" />
                      <span className="truncate">Total: ₹{fuelEntry.fuelItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0).toFixed(2)}</span>
                    </div>
                  )}
                  {fuelEntry.amountGivenToBunk && (
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                      <IndianRupee className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0" />
                      <span className="truncate">Given: ₹{parseFloat(fuelEntry.amountGivenToBunk).toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Fuel Items Summary */}
                {fuelEntry.fuelItems && fuelEntry.fuelItems.length > 0 && (
                  <div className="text-xs text-gray-500 mb-3 sm:mb-4">
                    <div className="flex items-center mb-1">
                      <Fuel className="h-3 w-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{fuelEntry.fuelItems.length} fuel item(s)</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
                      <span className="truncate">Hours: {fuelEntry.fuelItems.reduce((sum, item) => sum + parseFloat(item.vehicleHours || 0), 0).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Fuel Entry Info */}
                <div className="text-xs text-gray-400 border-t border-gray-100 pt-2 sm:pt-3">
                  <div className="truncate">Created: {new Date(fuelEntry.created_at || fuelEntry.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full">
              <div className="text-center py-8 sm:py-12">
                <div className="bg-gray-100 rounded-full p-2 sm:p-3 w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4">
                  <Plus className="h-6 w-6 sm:h-10 sm:w-10 text-gray-400" />
                </div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                  {searchTerm ? 'No fuel entries found' : 'No fuel entries yet'}
                </h3>
                <p className="text-sm sm:text-base text-gray-500 mb-3 sm:mb-4">
                  {searchTerm || filterVehicle !== 'all' || dateRange.startDate || dateRange.endDate
                    ? 'Try adjusting your search criteria, vehicle filter, or date filters' 
                    : 'Get started by adding your first fuel entry'
                  }
                </p>
                {!searchTerm && filterVehicle === 'all' && !dateRange.startDate && !dateRange.endDate && (
                  <button
                    onClick={handleAddFuelEntry}
                    className="btn btn-primary text-sm sm:text-base px-3 sm:px-4 py-2 sm:py-3"
                  >
                    Add Your First Fuel Entry
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fuel Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingFuelEntry ? 'Edit Fuel Entry' : 'Add New Fuel Entry'}
        size="max-w-4xl"
      >
        <FuelForm
          fuelEntry={editingFuelEntry}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          isSubmitting={isUpdating || isCreating}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Delete Fuel Entry"
        message={`Are you sure you want to delete the fuel entry for "${deleteModal.fuelEntry?.bunkName}"?`}
        itemName={deleteModal.fuelEntry?.bunkName || 'Fuel Entry'}
      />
    </div>
  )
}

export default FuelManagement
